import React, { useEffect } from 'react';
import { isProjectMappingInHelpCenterEnabled } from '@helpcenter/feature-flags';
import { getCloudId, getHelpCenterAri } from '@helpcenter/meta';
import { useIntl } from 'react-intl-next';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { usePathParam } from 'react-resource-router';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { withAnalyticsEvents } from '@atlaskit/analytics-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import Heading from '@atlaskit/heading';
import { Box, media, xcss } from '@atlaskit/primitives';
import { ExperienceSuccess } from '@atlassian/ufo';
import {
    REQUEST_FORMS_NEW_LIMIT,
    REQUEST_FORMS_RESULT_LIMIT,
    ResultList,
    sendSearchOperationalAnalyticEvents,
    sendSearchUIAnalyticEvents,
} from '../common';
import { searchExperience } from '../experiences';
import { RequestFormItem } from '../request-form-item';
import type { requestFormsSearchQuery } from './__generated__/requestFormsSearchQuery.graphql';
import messages from './messages';

export interface Props {
    term: string;
    updateResultsCount: (count: number, acknowledgement?: number) => void;
    createAnalyticsEvent?: CreateUIAnalyticsEvent;
}

export const RequestForms = ({ term, updateResultsCount, createAnalyticsEvent }: Props) => {
    const { formatMessage } = useIntl();
    const cloudId = getCloudId();
    const [portalId] = usePathParam('portalId');
    const data = useLazyLoadQuery<requestFormsSearchQuery>(
        graphql`
            query requestFormsSearchQuery(
                $cloudId: ID!
                $queryTerm: String!
                $limit: Int!
                $helpCenterAri: ID
                $portalId: String
            ) {
                helpObjectStore_searchRequestTypes(
                    cloudId: $cloudId
                    queryTerm: $queryTerm
                    limit: $limit
                    helpCenterAri: $helpCenterAri
                    portalId: $portalId
                ) {
                    __typename
                    ... on HelpObjectStoreRequestTypeSearchResults {
                        results {
                            id
                            metadata {
                                isExternal
                                searchStrategy
                            }
                            ...requestFormItemFragmentV2
                        }
                    }
                    ... on HelpObjectStoreSearchError {
                        message
                        extensions {
                            statusCode
                            errorType
                        }
                    }
                }
            }
        `,
        {
            cloudId,
            portalId,
            queryTerm: term,
            helpCenterAri: isProjectMappingInHelpCenterEnabled() ? getHelpCenterAri() : undefined,
            limit: FeatureGates.checkGate('jsm_request_type_limit_update_spike')
                ? REQUEST_FORMS_NEW_LIMIT
                : REQUEST_FORMS_RESULT_LIMIT,
        }
    );

    const requestForms =
        data.helpObjectStore_searchRequestTypes?.__typename === 'HelpObjectStoreRequestTypeSearchResults'
            ? data.helpObjectStore_searchRequestTypes.results
            : [];
    const showSection = requestForms.length > 0;

    useEffect(() => {
        if (data.helpObjectStore_searchRequestTypes?.__typename === 'HelpObjectStoreRequestTypeSearchResults') {
            let externalResourcesCount = 0;
            let firstPartySearchPlatformResourcesCount = 0;

            data.helpObjectStore_searchRequestTypes.results.forEach((requestForm) => {
                const { metadata } = requestForm;
                const { isExternal, searchStrategy } = metadata;

                if (isExternal) {
                    externalResourcesCount += 1;
                }
                if (searchStrategy === 'SEARCH_PLATFORM_KEYWORD_BASED') {
                    firstPartySearchPlatformResourcesCount += 1;
                }
            });

            sendSearchOperationalAnalyticEvents(
                {
                    action: 'requestTypeSearchSucceeded',
                    resourceCount: data.helpObjectStore_searchRequestTypes.results.length,
                },
                createAnalyticsEvent,
                portalId
            );

            if (showSection) {
                sendSearchUIAnalyticEvents(
                    {
                        externalResourcesCount,
                        firstPartySearchPlatformResourcesCount,
                        action: 'rendered',
                        resourceType: 'request-form',
                        resourceCount: data.helpObjectStore_searchRequestTypes.results.length,
                    },
                    createAnalyticsEvent,
                    portalId
                );
            }
        }
    }, [term, showSection, data, createAnalyticsEvent, portalId]);

    useEffect(() => {
        if (data.helpObjectStore_searchRequestTypes?.__typename === 'HelpObjectStoreSearchError') {
            const { extensions, message: errorMessage } = data.helpObjectStore_searchRequestTypes;
            const statusCode = extensions.length > 0 ? extensions[0].statusCode : undefined;
            const is4XXFailure = statusCode && statusCode >= 400 && statusCode < 500 ? true : false;
            sendSearchOperationalAnalyticEvents(
                {
                    errorMessage,
                    statusCode,
                    is4XXFailure,
                    action: 'requestTypeSearchFailed',
                    errorType: extensions.length > 0 ? extensions[0].errorType : undefined,
                },
                createAnalyticsEvent,
                portalId
            );
        }
    }, [createAnalyticsEvent, data, portalId]);

    useEffect(() => {
        updateResultsCount(requestForms.length, 1);
    }, [term, requestForms.length, updateResultsCount]);

    return (
        <>
            {showSection && (
                <Box xcss={containerStyles}>
                    <Box xcss={headingStyles}>
                        <Heading size="xxsmall" as={FeatureGates.checkGate('ff_a11y_hc_heading') ? 'h2' : undefined}>
                            {formatMessage(messages.requestForms)}
                        </Heading>
                    </Box>
                    <ResultList role="list">
                        {requestForms.map((resource, index) => {
                            const itemOrder = index + 1;
                            const key = resource.id;
                            return (
                                <li key={key}>
                                    <RequestFormItem itemOrder={itemOrder} term={term} result={resource} />
                                </li>
                            );
                        })}
                    </ResultList>
                </Box>
            )}
            <ExperienceSuccess experience={searchExperience} />
        </>
    );
};

const containerStyles = xcss({
    overflow: 'hidden',
    paddingTop: 'space.250',
    paddingRight: 'space.150',
    paddingBottom: 'space.150',
    paddingLeft: 'space.150',
    gap: 'space.150',
    borderColor: 'color.border',
    borderStyle: 'solid',
    borderRadius: 'radius.small',
    borderWidth: 'border.width',
    rowGap: 'space.100',
    [media.above.xxs]: { marginLeft: 'space.100', marginRight: 'space.100', width: '1fr' },
    [media.above.md]: {
        marginLeft: 'space.0',
        marginRight: 'space.0',
        width: '320px',
    },
});
const headingStyles = xcss({
    width: '100%',
    paddingRight: 'space.100',
    paddingBottom: 'space.050',
    paddingLeft: 'space.100',
});

export default withAnalyticsEvents()(RequestForms);
